import type { AppRouter } from '@ionq/trpc-console';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { createTRPCClient, httpLink, isTRPCClientError } from '@trpc/client';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import superjson from 'superjson';

// Same-origin console-backend namespace. Every serving edge (Vite proxy, dev
// Traefik, A/B router, the enterprise ingress mount) strips /api/console and
// the backend serves /trpc at root (see apps/console/README.md) — so this
// path works identically wherever console content renders, standalone or
// federated into another host.
const TRPC_SERVER_PATH = '/api/console/trpc';

// Module-level like enterprise's: the shell's SessionProvider registers it,
// and an embedding host registers its own through the ./session expose —
// asynchronously, after the remote import. A 401 landing before any
// handler is held and replayed on registration (null clears both), since
// the session-aware default never throws it to a boundary either.
let onSessionExpired: (() => void) | null = null;
let heldSessionExpiry = false;
export const setOnSessionExpired = (callback: (() => void) | null) => {
    onSessionExpired = callback;
    if (callback && heldSessionExpiry) callback();
    heldSessionExpiry = false;
};

export function isTRPCAuthError(error: unknown) {
    // Only a tRPC UNAUTHORIZED response is a session error. Anything else
    // (network blips, bugs in query functions) must surface through the
    // normal error path.
    if (!isTRPCClientError(error)) return false;
    return 'UNAUTHORIZED' === error.data?.code;
}

export function isTRPCForbiddenError(error: unknown) {
    if (!isTRPCClientError(error)) return false;
    return 'FORBIDDEN' === error.data?.code;
}

// A tRPC error carrying a 4xx response is deterministic — replaying the
// identical request yields the identical response. 5xx and transport errors
// may be transient and stay retryable.
function isTRPC4xxError(error: unknown) {
    if (!isTRPCClientError(error)) return false;
    const status = error.data?.httpStatus;
    return typeof status === 'number' && status >= 400 && status < 500;
}

// Runs from the caches' async failure path (after retries, never during
// render), so the callback may set React state directly.
const notifySessionError = (error: unknown) => {
    if (!isTRPCAuthError(error)) return;
    if (onSessionExpired) onSessionExpired();
    else heldSessionExpiry = true;
};

/** The default throwOnError (enterprise's policy): real failures go to the
 * nearest error boundary, but an expired session stays with the
 * expired-session dialog — the page keeps its last data under it. A query
 * overriding throwOnError composes this instead of replacing it. */
export const throwUnlessSessionError = (error: unknown) =>
    !isTRPCAuthError(error);

// Trimmed vs enterprise's client: the session query runs in the route
// loader, whose catch owns the auth/forbidden redirects.
export const queryClient = new QueryClient({
    queryCache: new QueryCache({ onError: notifySessionError }),
    mutationCache: new MutationCache({ onError: notifySessionError }),
    defaultOptions: {
        queries: {
            throwOnError: throwUnlessSessionError,
            retry: (failureCount, error) =>
                failureCount < 1 && !isTRPC4xxError(error),
        },
    },
});

export const trpc = createTRPCOptionsProxy<AppRouter>({
    client: createTRPCClient<AppRouter>({
        links: [httpLink({ url: TRPC_SERVER_PATH, transformer: superjson })],
    }),
    queryClient,
});

// Loader-only query (no observers): staleTime pins it for the session and
// gcTime keeps the observer-less entry from being collected into a surprise
// refetch. Switch and sign-out are full document loads, which reset it.
export const sessionUserQueryOptions = () =>
    trpc.users.me.queryOptions(undefined, {
        staleTime: Number.POSITIVE_INFINITY,
        gcTime: Number.POSITIVE_INFINITY,
    });
